import * as React from 'react';
import { useState, useEffect } from 'react';
import { Typography, Paper, Box, Button, Stack, Chip, CircularProgress, Alert } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ReceiverService from '../receiverService';

const receiverService = new ReceiverService();

export default function GNSSTools() {
  const [controllerStatus, setControllerStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restartingController, setRestartingController] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchStatus = () => {
    setLoading(true);
    setError('');

    receiverService.getControllerStatus()
      .then((controllerResp) => {
        setControllerStatus(controllerResp);
        setLoading(false);
      })
      .catch((error) => {
        setError('Failed to fetch service status');
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchStatus();
    // Refresh status every 10 seconds
    const interval = setInterval(fetchStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleControllerRestart = () => {
    setRestartingController(true);
    setError('');
    setSuccess('');
    receiverService.restartController()
      .then(() => {
        setSuccess('GNSS Controller service restarted successfully');
        setRestartingController(false);
        // Refresh status after a short delay
        setTimeout(fetchStatus, 2000);
      })
      .catch((error) => {
        setError('Failed to restart GNSS Controller service');
        setRestartingController(false);
      });
  };

  return (
    <React.Fragment>
      <h1 className="page-title"><span className="fw-semi-bold">GNSS Tools</span></h1>

      <Stack spacing={3} sx={{ maxWidth: 800 }}>
        {/* Global error/success messages */}
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        {/* GNSS Controller Service */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>GNSS Controller Service</Typography>

          {loading && !controllerStatus ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <Typography>Loading service status...</Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body1">Status:</Typography>
                {controllerStatus?.running ? (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="Running"
                    color="success"
                    size="small"
                  />
                ) : (
                  <Chip
                    icon={<ErrorIcon />}
                    label={controllerStatus?.status || 'Stopped'}
                    color="error"
                    size="small"
                  />
                )}
              </Box>

              <Button
                variant="contained"
                startIcon={restartingController ? <CircularProgress size={20} color="inherit" /> : <RestartAltIcon />}
                onClick={handleControllerRestart}
                disabled={restartingController}
              >
                {restartingController ? 'Restarting...' : 'Restart Service'}
              </Button>

              <Typography variant="caption" color="text.secondary">
                Restarting the GNSS Controller service will temporarily interrupt GNSS data processing.
                All configurations will be preserved.
              </Typography>
            </Stack>
          )}
        </Paper>
      </Stack>
    </React.Fragment>
  );
}
